import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables:', {
    hasUrl: !!supabaseUrl,
    hasKey: !!supabaseAnonKey
  });
}

// Initialize Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// API Key Management
export async function createApiKey(name = 'Default Key') {
  const apiKey = crypto.randomUUID();
  const keyHash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(apiKey)).then(hash => {
    return Array.from(new Uint8Array(hash), b => b.toString(16).padStart(2, '0')).join('');
  });

  const { data: key, error } = await supabase
    .from('api_keys')
    .insert([
      {
        name,
        key_hash: keyHash,
        active: true
      }
    ])
    .select()
    .single();

  if (error) {
    throw error;
  }

  return {
    apiKey,
    keyData: key
  };
}

export async function validateApiKey(apiKey) {
  const keyHash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(apiKey)).then(hash => {
    return Array.from(new Uint8Array(hash), b => b.toString(16).padStart(2, '0')).join('');
  });

  const { data: key, error } = await supabase
    .from('api_keys')
    .select('*')
    .eq('key_hash', keyHash)
    .eq('active', true)
    .single();

  if (error) {
    return false;
  }

  return !!key;
}

// Usage Stats
export async function updateUsageStats(apiKeyId) {
  const { data, error } = await supabase.rpc('increment_api_usage', {
    p_api_key_id: apiKeyId
  });

  if (error) throw error;
  return data;
}

// Job Management
export async function createJob(inputUrl) {
  const { data: job, error } = await supabase
    .from('jobs')
    .insert([
      {
        status: 'pending',
        input_url: inputUrl,
      }
    ])
    .select()
    .single();

  if (error) throw error;
  return job;
}

export async function updateJobStatus(jobId, status, resultUrl = null, errorMessage = null) {
  const updates = {
    status,
    updated_at: new Date().toISOString(),
  };

  if (status === 'completed') {
    updates.result_url = resultUrl;
    updates.completed_at = new Date().toISOString();
  } else if (status === 'failed') {
    updates.error_message = errorMessage;
  }

  const { data: job, error } = await supabase
    .from('jobs')
    .update(updates)
    .eq('id', jobId)
    .select()
    .single();

  if (error) throw error;
  return job;
}

// Rate Limiting
export async function getRateLimitInfo(apiKeyId) {
  const windowMs = parseInt(process.env.REACT_APP_API_RATE_LIMIT_WINDOW) || 60000;
  const windowStart = new Date(Date.now() - windowMs).toISOString();

  const { count, error } = await supabase
    .from('rate_limits')
    .select('*', { count: 'exact' })
    .eq('api_key_id', apiKeyId)
    .gte('window_start', windowStart);

  if (error) throw error;
  return { count, windowStart };
}

export async function recordRateLimit(apiKeyId) {
  const { data, error } = await supabase
    .from('rate_limits')
    .insert([
      {
        api_key_id: apiKeyId,
        window_start: new Date().toISOString(),
      }
    ])
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Cleanup function to be run periodically
export async function cleanupRateLimits() {
  const windowMs = parseInt(process.env.REACT_APP_API_RATE_LIMIT_WINDOW) || 60000;
  const cutoff = new Date(Date.now() - windowMs).toISOString();

  const { error } = await supabase
    .from('rate_limits')
    .delete()
    .lt('window_start', cutoff);

  if (error) throw error;
}
